import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const KEY = "mtaa-theme";
const THEMES = ["light", "dark", "system"];

function systemDark() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(theme) {
  const dark = theme === "dark" || (theme === "system" && systemDark());
  document.documentElement.classList.toggle("dark", dark);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", dark ? "#101113" : "#0A7A50");
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem(KEY);
    return THEMES.includes(saved) ? saved : "system";
  });

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => { if ((localStorage.getItem(KEY) || "system") === "system") applyTheme("system"); };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setTheme = useCallback((t) => {
    if (!THEMES.includes(t)) return;
    localStorage.setItem(KEY, t);
    setThemeState(t);
    applyTheme(t);
  }, []);

  // Adopt a preference coming from the user's account (cross-device) without a flash.
  const adoptTheme = useCallback((t) => {
    if (!THEMES.includes(t)) return;
    localStorage.setItem(KEY, t);
    setThemeState(t);
    applyTheme(t);
  }, []);

  const isDark = theme === "dark" || (theme === "system" && systemDark());

  return (
    <ThemeContext.Provider value={{ theme, setTheme, adoptTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}
